"use client";

import React, { useEffect, useState } from "react";
import { Brain, Activity, TriangleAlert, Zap, Terminal, CircleCheck, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = ["Detailed dimensions for accurate scaling", "Reference images for design inspiration", "Material preference (Gold, Silver, Platinum)", "Specific stone sizes and setting types"];
const nodes = [{
  id: "ph-monitor",
  icon: Activity,
  label: "CAD Precision",
  value: "0.01mm",
  status: "High Accuracy",
  color: "text-primary",
  position: "top-[10%] left-[10%]",
  delay: "delay-0"
}, {
  id: "hazard-detect",
  icon: TriangleAlert,
  label: "Print Check",
  value: "Ready",
  status: "Wall thickness verified",
  color: "text-secondary",
  position: "top-[20%] right-[5%]",
  delay: "delay-150"
}, {
  id: "kinetics",
  icon: Zap,
  label: "File Formats",
  value: ".3DM",
  status: "Industry Standard",
  color: "text-chart-3",
  position: "bottom-[15%] left-[20%]",
  delay: "delay-300"
}];
export default function AiLabAssistant() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: isVisualVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [typedText, setTypedText] = useState("");
  const fullText = "> INITIALIZING DESIGN PROTOCOL...";
  useEffect(() => {
    if (isTextVisible) {
      let i = 0;
      const typingInterval = setInterval(() => {
        if (i < fullText.length) {
          setTypedText(fullText.substring(0, i + 1));
          i++;
        } else {
          clearInterval(typingInterval);
        }
      }, 50);
      return () => clearInterval(typingInterval);
    }
  }, [isTextVisible]);
  return <section data-section-id="252401" id="submission-guidelines" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
      </div>
      
      {}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-md blur-3xl animate-pulse pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/5 rounded-md blur-3xl animate-pulse delay-700 pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {}
          <div ref={textRef} className={`transition-all duration-1000 transform ${isTextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary font-mono text-sm mb-6">
              <Terminal className="w-4 h-4" />
              <span>{typedText}</span>
              <span className="w-2 h-4 bg-primary animate-pulse inline-block ml-1" />
            </div>
            
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Project <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Submission</span> Guidelines
            </h2>
            
            <p className="text-muted-foreground text-lg md:text-xl mb-8 font-default max-w-xl">
              To ensure the highest quality CAD models and 3D prints, please follow these technical requirements. Clear specifications allow us to deliver your .3dm and .stl files with surgical precision.
            </p>
            
            <ul className="space-y-4 mb-10">
              {features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                  <CircleCheck className="w-6 h-6 text-secondary shrink-0 mt-0.5" />
                  <span className="text-foreground/90 font-medium font-default">{feature}</span>
                </li>)}
            </ul>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/StartYourProjectPage">
                <Button size="lg" className="font-default tracking-wide group bg-primary text-primary-foreground rounded-md shadow-lg">
                  Submit Your Job
                  <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="font-default tracking-wide bg-background/50 backdrop-blur-md border-border/50 rounded-md shadow-md">
                Pricing Details
              </Button>
            </div>
          </div>

          {}
          <div ref={visualRef} className={`relative h-[500px] lg:h-[600px] flex items-center justify-center transition-all duration-1000 delay-300 transform ${isVisualVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            
            {}
            <div className="relative z-20 flex items-center justify-center">
              {}
              <div className="absolute inset-0 rounded-md border border-primary/20 w-64 h-64 animate-[spin_20s_linear_infinite]" />
              <div className="absolute inset-0 rounded-md border border-secondary/20 w-80 h-80 animate-[spin_25s_linear_infinite_reverse]" />
              
              {}
              <div className="w-48 h-48 rounded-md bg-card/80 backdrop-blur-xl border border-primary/30 shadow-2xl flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10 opacity-50" />
                <Brain className="w-24 h-24 text-primary drop-shadow-[0_0_15px_rgba(var(--color-primary),0.4)] animate-pulse" />
              </div>
            </div>

            {}
            {nodes.map((node, i) => <div key={node.id} data-index={i} className={`absolute z-30 ${node.position} animate-bounce ${node.delay}`} style={{
            animationDuration: '4s'
          }}>
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 px-4 py-2 rounded-md bg-card/60 backdrop-blur-md border border-border/50 shadow-md hover:border-secondary/50 transition-colors group">
                      <node.icon className={`w-5 h-5 ${node.color} group-hover:scale-110 transition-transform`} />
                      <span className="font-mono text-sm font-medium">{node.label}</span>
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-64 bg-popover/95 backdrop-blur-xl border-border shadow-2xl rounded-md">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-default font-semibold text-sm">{node.label}</h4>
                      <node.icon className={`w-4 h-4 ${node.color}`} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-2xl font-mono font-bold text-foreground">{node.value}</p>
                      <p className="text-xs text-muted-foreground font-default">{node.status}</p>
                    </div>
                    <div className="mt-4 h-1 w-full bg-muted rounded-md overflow-hidden">
                      <div className={`h-full bg-current ${node.color} w-3/4 animate-pulse`} />
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>)}

            {}
            <svg className="absolute inset-0 w-full h-full z-10 pointer-events-none opacity-20" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M50,50 L20,20" stroke="currentColor" strokeWidth="0.1" className="text-primary" strokeDasharray="2 2" />
              <path d="M50,50 L80,30" stroke="currentColor" strokeWidth="0.1" className="text-secondary" strokeDasharray="2 2" />
              <path d="M50,50 L30,80" stroke="currentColor" strokeWidth="0.1" className="text-chart-3" strokeDasharray="2 2" />
            </svg>

          </div>
        </div>
      </div>
    </section>;
}